import { useEffect, useState, useMemo, useRef } from "react"
import { useParams, useLocation, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { useLoading } from "@/hooks/useLoading"
import { useToast } from "@/contexts/ToastContext"
import { getPessoas } from "@/services/companyService"
import { approveInscricao, reproveInscricao } from "@/modules/Events/service/EventsService"
import { EventoDataType } from "@/types/events"
import { PessoaDataType } from "@/types/company"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { ApprovalModal } from "@/modules/Events/components/ApprovalModal"
import { RefusalModal } from "@/modules/Events/components/RefusalModal"
import { Icon } from "@/components/Atoms/Icon"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { toast } from "sonner"
import { FichaTecnicaPanel } from "@/modules/Events/components/FichaTecnicaPanel"

const StatusBadge = ({ status }: { status: string }) => {
  const styles =
    status === "presença autorizada"
      ? "bg-emerald-500/10 text-emerald-500"
      : status === "pendente de aprovação"
        ? "bg-yellow-500/10 text-yellow-500"
        : "bg-zinc-500/10 text-zinc-500"

  return (
    <span
      className={`text-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${styles}`}
    >
      {status}
    </span>
  )
}

export default function EventSubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { state: locationState } = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showLoading, hideLoading } = useLoading()

  const [evento, setEvento] = useState<EventoDataType | undefined>(
    (locationState as any)?.evento as EventoDataType | undefined
  )

  const [subscriptions, setSubscriptions] = useState<PessoaDataType[]>([])
  const [selectedPeopleIds, setSelectedPeopleIds] = useState<number[]>([])
  const [individualSelection, setIndividualSelection] = useState<number | null>(null)
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false)
  const [isRefusalModalOpen, setIsRefusalModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Guarda síncrona contra duplo clique (o state só atualiza no próximo render)
  const submittingRef = useRef(false)
  const [subscriptionSearch, setSubscriptionSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("todos")

  const fetchSubscriptions = async () => {
    if (!evento) return
    showLoading()
    try {
      const { data } = await getPessoas(user.empId, {
        id_origem: evento.id,
        origem: evento.origem,
      })
      setSubscriptions(data.data)
      setSelectedPeopleIds([])
    } catch {
      toast.error("Erro ao carregar inscrições")
    } finally {
      hideLoading()
    }
  }

  useEffect(() => {
    fetchSubscriptions()
  }, [id])

  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter((sub) => {
      const matchSearch =
        !subscriptionSearch ||
        sub.pes_nome.toLowerCase().includes(subscriptionSearch.toLowerCase()) ||
        sub.pes_email.toLowerCase().includes(subscriptionSearch.toLowerCase()) ||
        (sub.emp_fantasia ?? "").toLowerCase().includes(subscriptionSearch.toLowerCase())
      const matchStatus = statusFilter === "todos" || sub.status_origem === statusFilter
      return matchSearch && matchStatus
    })
  }, [subscriptions, subscriptionSearch, statusFilter])

  const approvedCount = useMemo(
    () => subscriptions.filter((s) => s.status_origem === "presença autorizada").length,
    [subscriptions]
  )
  const rejectedCount = useMemo(
    () => subscriptions.filter((s) => s.status_origem === "presença não autorizada").length,
    [subscriptions]
  )
  const pendingCount = useMemo(
    () => subscriptions.filter((s) => s.status_origem === "pendente de aprovação").length,
    [subscriptions]
  )
  const total = subscriptions.length
  const progresso = total > 0 ? ((approvedCount + rejectedCount) / total) * 100 : 0

  const handleSelectPerson = (personId: number) => {
    setSelectedPeopleIds((prev) =>
      prev.includes(personId) ? prev.filter((pId) => pId !== personId) : [...prev, personId]
    )
  }

  const handleSelectAll = () => {
    const pendings = filteredSubscriptions
      .filter((s) => s.status_origem === "pendente de aprovação")
      .map((s) => s.pes_id)
    setSelectedPeopleIds(selectedPeopleIds.length === pendings.length ? [] : pendings)
  }

  const handleIndividualAction = (personId: number, type: "approve" | "refuse") => {
    setIndividualSelection(personId)
    if (type === "approve") {
      const skipModal = localStorage.getItem("hideApprovalModal") === "true"
      skipModal ? handleConfirmApproval(personId) : setIsApprovalModalOpen(true)
    } else {
      setIsRefusalModalOpen(true)
    }
  }

  const handleBulkAction = (type: "approve" | "refuse") => {
    if (selectedPeopleIds.length === 0) {
      toast.error("Selecione ao menos um colaborador")
      return
    }
    setIndividualSelection(null)
    if (type === "approve") {
      const skipModal = localStorage.getItem("hideApprovalModal") === "true"
      skipModal ? handleConfirmApproval() : setIsApprovalModalOpen(true)
    } else {
      setIsRefusalModalOpen(true)
    }
  }

  const handleConfirmApproval = async (idOverride?: number) => {
    if (!evento || submittingRef.current) return
    submittingRef.current = true
    setIsSubmitting(true)
    const ids = idOverride
      ? [idOverride]
      : individualSelection
        ? [individualSelection]
        : selectedPeopleIds
    try {
      await approveInscricao({
        pes_id: ids,
        originador_id: user.pesId,
        origem_id: evento.id,
        origem: evento.origem,
        status: "aprovar",
      })
      toast.success(`${ids.length} inscrição(ões) aprovada(s)!`)
      setSelectedPeopleIds([])
      fetchSubscriptions()
    } catch {
      toast.error("Erro ao aprovar inscrições")
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
      setIsApprovalModalOpen(false)
      setIndividualSelection(null)
    }
  }

  const handleConfirmRefusal = async (reason: string) => {
    if (!evento || submittingRef.current) return
    submittingRef.current = true
    setIsSubmitting(true)
    const ids = individualSelection ? [individualSelection] : selectedPeopleIds
    try {
      await reproveInscricao({
        pes_id: ids,
        originador_id: user.pesId,
        origem_id: evento.id,
        origem: evento.origem,
        status: "reprovar",
        motivo: reason,
      })
      toast.success("Inscrição(ões) recusada(s)!")
      setSelectedPeopleIds([])
      fetchSubscriptions()
    } catch {
      toast.error("Erro ao recusar inscrições")
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
      setIsRefusalModalOpen(false)
      setIndividualSelection(null)
    }
  }

  const canManageFile = user.gestaoApp
  const isEventPast = evento ? new Date(evento.data_fim) < new Date() : false

  return (
    <div className="animate-fade-in space-y-6 pb-8">
      <PageHeader title={evento?.descricao ?? "Detalhes da Inscrição"} />

      <div className="px-6">
        <button
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-white"
        >
          <i className="fi fi-ts-arrow-left transition-transform group-hover:-translate-x-1" />
          Voltar
        </button>
      </div>

      {isEventPast && (
        <div className="mx-6 flex items-center gap-2 rounded-lg border border-zinc-500/30 bg-zinc-500/10 px-4 py-2.5 text-sm text-zinc-400">
          <i className="fi fi-ts-lock flex items-center text-base" />
          Este evento já foi encerrado. As inscrições são somente leitura.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 px-6 xl:grid-cols-[1fr_420px]">
        {/* ── Left column: progress + list ── */}
        <div className="min-w-0 space-y-4">
          {/* Summary with progress */}
          <div className="space-y-3 rounded-lg border border-border bg-surface p-4">
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-950 p-0.5 shadow-sm">
              <div
                className="h-full rounded-full bg-gradient-to-r from-slate-900 to-orange-500 transition-all duration-700"
                style={{ width: progresso + "%" }}
              />
              <div className="absolute left-0 top-0 size-full rounded-full border-t border-x-transparent border-b-gray-900 border-t-gray-700 bg-gradient-to-t from-black/10 to-gray-400/20" />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="size-2 shrink-0 rounded-full bg-green-500" />
                <span className="text-muted-foreground">
                  Aprovados: <strong className="text-foreground">{approvedCount}</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2 shrink-0 rounded-full bg-red-500" />
                <span className="text-muted-foreground">
                  Recusados: <strong className="text-foreground">{rejectedCount}</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2 shrink-0 rounded-full bg-yellow-500" />
                <span className="text-muted-foreground">
                  Pendentes: <strong className="text-foreground">{pendingCount}</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2 shrink-0 rounded-full bg-zinc-500" />
                <span className="text-muted-foreground">
                  Total: <strong className="text-foreground">{total}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Filters + bulk actions */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex min-w-0 flex-1 gap-2">
              <Input
                placeholder="Buscar por nome, email ou empresa..."
                value={subscriptionSearch}
                onChange={(e) => setSubscriptionSearch(e.target.value)}
              />
              <Select
                value={statusFilter}
                onValueChange={setStatusFilter}
              >
                <SelectTrigger className="w-44 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente de aprovação">Pendentes</SelectItem>
                  <SelectItem value="presença autorizada">Aprovados</SelectItem>
                  <SelectItem value="presença não autorizada">Recusados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <AnimatePresence>
              {!isEventPast && selectedPeopleIds.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex w-full items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 p-2 sm:w-auto"
                >
                  <span className="px-2 text-sm font-medium">
                    {selectedPeopleIds.length} selecionados
                  </span>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={isSubmitting}
                    onClick={() => handleBulkAction("refuse")}
                  >
                    Reprovar Lote
                  </Button>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={isSubmitting}
                    onClick={() => handleBulkAction("approve")}
                  >
                    Aprovar Lote
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile cards */}
          <div className="flex flex-col gap-2 md:hidden">
            {filteredSubscriptions.map((item) => (
              <div
                key={item.pes_id}
                className="rounded-lg border bg-surface p-3"
              >
                <div className="flex items-start gap-3">
                  {!isEventPast && item.status_origem === "pendente de aprovação" && (
                    <Checkbox
                      checked={selectedPeopleIds.includes(item.pes_id)}
                      onCheckedChange={() => handleSelectPerson(item.pes_id)}
                      className="mt-1 shrink-0"
                    />
                  )}
                  <img
                    src={item.pes_foto_url}
                    className="size-10 shrink-0 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="truncate font-medium">{item.pes_nome}</p>
                    <p className="truncate text-xs text-muted-foreground">{item.pes_email}</p>
                    <div className="flex items-center justify-between pt-1">
                      <StatusBadge status={item.status_origem} />
                      {!isEventPast && item.status_origem === "pendente de aprovação" && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-500"
                            disabled={isSubmitting}
                            onClick={() => handleIndividualAction(item.pes_id, "refuse")}
                          >
                            <Icon name="circle-xmark" />
                          </Button>
                          <Button
                            variant="ghost"
                            className="h-8 w-8 p-0 text-green-500"
                            disabled={isSubmitting}
                            onClick={() => handleIndividualAction(item.pes_id, "approve")}
                          >
                            <Icon name="check-circle" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filteredSubscriptions.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Nenhuma inscrição encontrada.
              </div>
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-md border md:block">
            <div
              className={`grid items-center bg-muted/30 p-3 text-xs font-bold uppercase ${isEventPast ? "grid-cols-[1fr_1fr_130px]" : "grid-cols-[40px_1fr_1fr_130px_120px]"}`}
            >
              {!isEventPast && (
                <Checkbox
                  checked={
                    selectedPeopleIds.length > 0 &&
                    selectedPeopleIds.length ===
                      filteredSubscriptions.filter((s) => s.status_origem === "pendente de aprovação")
                        .length
                  }
                  onCheckedChange={handleSelectAll}
                />
              )}
              <span>Colaborador</span>
              <span>Email</span>
              <span className="text-center">Status</span>
              {!isEventPast && <span className="text-right">Ações</span>}
            </div>

            {filteredSubscriptions.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Nenhuma inscrição encontrada.
              </div>
            ) : (
              filteredSubscriptions.map((item) => (
                <div
                  key={item.pes_id}
                  className={`grid items-center border-t p-3 hover:bg-muted/5 ${isEventPast ? "grid-cols-[1fr_1fr_130px]" : "grid-cols-[40px_1fr_1fr_130px_120px]"}`}
                >
                  {!isEventPast && (
                    <Checkbox
                      disabled={item.status_origem !== "pendente de aprovação"}
                      checked={selectedPeopleIds.includes(item.pes_id)}
                      onCheckedChange={() => handleSelectPerson(item.pes_id)}
                    />
                  )}
                  <div className="flex items-center gap-2 truncate">
                    <img
                      src={item.pes_foto_url}
                      className="size-6 rounded-full object-cover"
                    />
                    <span className="truncate font-medium">{item.pes_nome}</span>
                  </div>
                  <span className="truncate text-sm text-muted-foreground">{item.pes_email}</span>
                  <div className="flex justify-center">
                    <StatusBadge status={item.status_origem} />
                  </div>
                  {!isEventPast && (
                    <div className="flex justify-end gap-1">
                      {item.status_origem === "pendente de aprovação" && (
                        <>
                          <Button
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500"
                            disabled={isSubmitting}
                            onClick={() => handleIndividualAction(item.pes_id, "refuse")}
                          >
                            <Icon name="circle-xmark" />
                          </Button>
                          <Button
                            variant="ghost"
                            className="h-7 w-7 p-0 text-green-500"
                            disabled={isSubmitting}
                            onClick={() => handleIndividualAction(item.pes_id, "approve")}
                          >
                            <Icon name="check-circle" />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Right column: Ficha Técnica ── */}
        {(evento?.url_ficha_tecnica || canManageFile) && evento && (
          <div className="overflow-hidden rounded-xl border border-purple-500/30 bg-purple-500/5 xl:sticky xl:top-4 xl:self-start">
            <div className="flex items-center gap-2 border-b border-purple-500/20 px-4 py-3">
              <i className="fi fi-ts-document flex items-center text-base text-purple-500" />
              <span className="font-semibold text-purple-500">Ficha Técnica</span>
            </div>
            <div className="flex flex-col gap-4 p-4">
              <FichaTecnicaPanel
                evento={evento}
                canManage={canManageFile}
                onUpdate={(url) =>
                  setEvento((prev) => (prev ? { ...prev, url_ficha_tecnica: url } : prev))
                }
              />
            </div>
          </div>
        )}
      </div>

      <ApprovalModal
        open={isApprovalModalOpen}
        onConfirm={() => handleConfirmApproval()}
        onClose={() => {
          setIsApprovalModalOpen(false)
          setIndividualSelection(null)
        }}
      />
      <RefusalModal
        isOpen={isRefusalModalOpen}
        onConfirm={handleConfirmRefusal}
        isConfirming={isSubmitting}
        onClose={() => {
          setIsRefusalModalOpen(false)
          setIndividualSelection(null)
        }}
      />
    </div>
  )
}
